import React, { useState, useEffect, useContext, useRef } from 'react';
import './style.css';
import { CircleX } from 'lucide-react';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import ProjectCardSolvingFor from './ProjectCardSolvingFor/ProjectCardSolvingFor';
import ProjectCardCosts from './ProjectCardCosts/ProjectCardCosts';
import CleanProjectNames from '/src/services/CleanProjectNames';
import ProjectCardPayoff from './ProjectCardPayoff/ProjectCardPayoff';
import ProjectCardDiscounts from './ProjectCardDiscounts/ProjectCardDiscounts';
import ProjectGettingItDone from './ProjectGettingItDone/ProjectGettingItDone';
import ProjectResources from './ProjectResources/ProjectResources';
import { FetchThumbtackListings } from '/src/services/FetchThumbtackListings';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';

const SECTION_BUTTONS = [
    { id: 'solving-for', text: "What You're Solving For" },
    { id: 'cost', text: 'Project Cost vs. Payoff' },
    // { id: 'payoff', text: 'Project Payoff' },
    { id: 'discounts', text: 'Available Discounts' },
    { id: 'getting-it-done', text: 'How to Get It Done' },
    { id: 'resources', text: 'Helpful Resources' }
];

const ProjectWrapper = ({ inline = false, children, handleOverlayClick, onClose }) => {
    return (
        <>
            {!inline &&
                <div className="project-modal-overlay" onClick={handleOverlayClick}>
                    <div className="project-modal-container">
                        <button className="project-modal-close-btn" onClick={onClose}>
                            <CircleX size={40} className="project-modal-close-icon" />
                        </button>
                        {children}
                    </div>
                </div>
            }
            {inline &&
                <div className="project-container-inline">
                    {children}
                </div>
            }
        </>
    )
}

const ProjectCard = ({ propertyData, project, onClose, inline = false, handleUrlSubsectionChange }) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Get the portalData from the AddressSearchContext
    const { portalData, LOG_GA_EVENT } = useContext(AddressSearchContext);
    const { houseCategories, icons } = portalData;
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();

    // useEffect to log when the project card is opened
    useEffect(() => {
        const gaAdditionalParams = {
            projectId: project.project_id,
            projectName: project.project_name,
        }
        handleLoggingGaEventFromProperty('project_opened', gaAdditionalParams);
    }, []);

    let houseCategoryIcon = project.house_category_icon;
    if (!houseCategoryIcon) {
        const house_category_id = project.project_home_category_id;
        const house_category_selected = house_category_id ? houseCategories[house_category_id] : null;
        const house_category_selected_icon_id = house_category_selected ? house_category_selected.icon : null;
        const house_category_selected_icon = house_category_selected_icon_id ? icons[house_category_selected_icon_id] : null;
        houseCategoryIcon = house_category_selected_icon;
    }

    if (houseCategoryIcon) {
        project.house_category_icon = houseCategoryIcon;
    }


    // Load data from local storage with safety checks
    // const project_skus_local_storage = JSON.parse(localStorage.getItem('skus')) || [];
    // const companies_local_storage = JSON.parse(localStorage.getItem('companies')) || [];
    // const icons_local_storage = JSON.parse(localStorage.getItem('icons')) || {};
    // const resources_local_storage = JSON.parse(localStorage.getItem('resources')) || [];
    const resources_local_storage = portalData.resources || [];

    const [selectedSection, setSelectedSection] = useState('solving-for');
    const [ttListingsLoaded, setTtListingsLoaded] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // useEffect to handle url changes when a section is clicked
    useEffect(() => {
        handleUrlSubsectionChange(selectedSection);
    }, [selectedSection]);

    const dropdownRef = useRef(null);

    // Add this useEffect inside your component
    useEffect(() => {
        if (!dropdownOpen) return;

        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Whent the project changes we want to set the selected section to the first section and load the thumbtack listings
    useEffect(() => {
        setSelectedSection('solving-for');
        
        const fetchListings = async () => {
            console.log('Fetching Thumbtack listings for project:', project);
            const zip_code = propertyData.data.property_details.zip;
            const project_ids = [project.project_id];
            try {
                const ttListingStatus = await FetchThumbtackListings(zip_code, project_ids);
                if (ttListingStatus) {
                    setTtListingsLoaded(true);
                }
            } catch (error) {
                console.error('Error fetching Thumbtack listings:', error);
                setTtListingsLoaded(false);
            }
        };

        fetchListings(); // Fire and forget - won't block rendering
        
    }, [project]);

    const handleOverlayClick = (e) => {
        // Only close if clicking the overlay itself, not its children
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const project_name = CleanProjectNames(project.project_name);

    // Get the resources from the project with safety check
    const project_resources = project.project_associated_resources || [];

    // Get the project house category and associated climate risks with safety checks
    const project_home_category = project.project_home_category || '';
    const project_climate_risks_refined = (project.project_associated_climate_risks || [])
        .map(risk => risk?.risk_name)
        .filter(Boolean);

    // First, get directly associated resources
    const direct_resources = project_resources
        .map(resource_id => resources_local_storage.find(r => r?.id === resource_id))
        .filter(Boolean);

    // If we need more resources, find additional relevant ones
    let additional_resources = [];
    if (direct_resources.length < 4) {
        // First attempt: climate risk matching
        additional_resources = resources_local_storage
            .filter(resource => {
                if (!resource) return false;

                const isDuplicate = direct_resources.some(dr => dr?.id === resource.id);
                if (isDuplicate) return false;

                const isNotTakingAction = resource.sub_category !== "Taking Action";
                const matchesHomeCategory = Array.isArray(resource.associated_house_categories) &&
                    resource.associated_house_categories.includes(project_home_category);
                const hasMatchingClimateRisk = Array.isArray(resource.associated_climate_risks) &&
                    resource.associated_climate_risks.some(
                        risk => project_climate_risks_refined.includes(risk)
                    );

                return isNotTakingAction && matchesHomeCategory && hasMatchingClimateRisk;
            });

        // If still need more resources, try second criteria
        if ((direct_resources.length + additional_resources.length) < 4) {
            const more_resources = resources_local_storage
                .filter(resource => {
                    if (!resource) return false;

                    // Check for duplicates in both direct and first additional resources
                    const isDuplicate = [...direct_resources, ...additional_resources]
                        .some(dr => dr?.id === resource.id);
                    if (isDuplicate) return false;

                    // Check home category match
                    const matchesHomeCategory = Array.isArray(resource.associated_house_categories) &&
                        resource.associated_house_categories.includes(project_home_category);

                    // Check resource type or sub-category criteria
                    const matchesTypeOrCategory =
                        resource.resource_type === "Maintenance" ||
                        resource.sub_category === "Know the Risk" ||
                        resource.sub_category === "Being Prepared";

                    return matchesHomeCategory && matchesTypeOrCategory;
                });

            // Add these new resources to additional_resources
            additional_resources = [...additional_resources, ...more_resources];
        }
    }

    // Combine and limit to 4 resources
    const enriched_resources = [
        ...direct_resources,
        ...additional_resources
    ];

    return (
        <ProjectWrapper inline={inline} handleOverlayClick={handleOverlayClick} onClose={onClose}>
            <div className="project-modal-content">
                <div className="project-modal-content">
                    <div className={`project-modal-hero ${deviceType}`}>
                        <img
                            src={CreateCloudfrontUrl(project.project_image)}
                            alt={project.project_name}
                            className="project-modal-hero-image"
                        />
                        <div className="project-modal-hero-overlay">
                            <div className="project-modal-hero-content">
                                <h2 className={`project-modal-title ${deviceType} text-align-center`}>{project_name}</h2>
                                <div className={`project-modal-installation-tag ${project.project_diy_friendly ? 'diy' : 'professional'}`}>
                                    <span className="project-modal-installation-tag-text">
                                        {project.project_diy_friendly ? 'DIY FRIENDLY' : 'PROFESSIONAL INSTALLATION RECOMMENDED'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* <div className="project-modal-info">
                        <p className="project-modal-description">{project.project_solving_for}</p>
                    </div> */}
                    <div className={`project-modal-nav ${deviceType}`}>
                        {deviceType === 'mobile' ? (
                            <div className="project-modal-nav-mobile-dropdown">
                                <button
                                    className="project-modal-nav-mobile-selected box-shadow-standard"
                                    onClick={() => setDropdownOpen((open) => !open)}
                                    aria-haspopup="listbox"
                                    aria-expanded={dropdownOpen}
                                >
                                    {SECTION_BUTTONS.find(b => b.id === selectedSection).text}
                                    <span className="project-modal-nav-mobile-arrow" aria-hidden="true">
                                        {/* Simple down arrow SVG */}
                                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                            <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                                        </svg>
                                    </span>
                                </button>
                                {dropdownOpen && (
                                    <div
                                        className="project-modal-nav-mobile-dropdown-list"
                                        role="listbox"
                                        ref={dropdownRef}
                                    >
                                        {SECTION_BUTTONS.filter(b => b.id !== selectedSection).map(button => (
                                            <button
                                                key={button.id}
                                                className="project-modal-nav-mobile-dropdown-btn box-shadow-standard"
                                                onClick={() => {
                                                    setSelectedSection(button.id);
                                                    setDropdownOpen(false);
                                                }}
                                                role="option"
                                            >
                                                {button.text}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            SECTION_BUTTONS.map(button => (
                                <button
                                    key={button.id}
                                    className={`text-align-center box-shadow-standard project-modal-nav-btn ${selectedSection === button.id ? 'project-modal-nav-btn--active' : ''}`}
                                    onClick={() => setSelectedSection(button.id)}
                                >
                                    {button.text}
                                </button>
                            ))
                        )}
                    </div>
                    {selectedSection === 'solving-for' && <ProjectCardSolvingFor project={project} />}
                    {selectedSection === 'cost' && <ProjectCardCosts project={project} />}
                    {selectedSection === 'payoff' && <ProjectCardPayoff project={project} />}
                    {selectedSection === 'discounts' && <ProjectCardDiscounts project={project} />}
                    {selectedSection === 'getting-it-done' && <ProjectGettingItDone propertyData={propertyData} ttListingsLoaded={ttListingsLoaded} project={project} />}
                    {selectedSection === 'resources' && <ProjectResources project={project} resources={enriched_resources} />}
                </div>
            </div>
        </ProjectWrapper>
        // <div className="project-modal-overlay" onClick={handleOverlayClick}>
        //     <div className="project-modal-container">
        //         <button className="project-modal-close-btn" onClick={onClose}>
        //             <CircleX size={40} className="project-modal-close-icon" />
        //         </button>
        //         <div className="project-modal-content">
        //             <div className="project-modal-hero">
        //                 <img
        //                     src={CreateCloudfrontUrl(project.project_image)}
        //                     alt={project.project_name}
        //                     className="project-modal-hero-image"
        //                 />
        //                 <div className="project-modal-hero-overlay">
        //                     <div className="project-modal-hero-content">
        //                         <h2 className="project-modal-title">{project_name}</h2>
        //                         <div className={`project-modal-installation-tag ${project.project_diy_friendly ? 'diy' : 'professional'}`}>
        //                             <span className="project-modal-installation-tag-text">
        //                                 {project.project_diy_friendly ? 'DIY FRIENDLY' : 'PROFESSIONAL INSTALLATION RECOMMENDED'}
        //                             </span>
        //                         </div>
        //                     </div>
        //                 </div>
        //             </div>
        //             {/* <div className="project-modal-info">
        //                 <p className="project-modal-description">{project.project_solving_for}</p>
        //             </div> */}
        //             <div className="project-modal-nav">
        //                 {SECTION_BUTTONS.map(button => (
        //                     <button
        //                         key={button.id}
        //                         className={`text-align-center box-shadow-standard project-modal-nav-btn ${selectedSection === button.id ? 'project-modal-nav-btn--active' : ''}`}
        //                         onClick={() => setSelectedSection(button.id)}
        //                     >
        //                         {button.text}
        //                     </button>
        //                 ))}
        //             </div>
        //             {selectedSection === 'solving-for' && <ProjectCardSolvingFor project={project} />}
        //             {selectedSection === 'cost' && <ProjectCardCosts project={project} />}
        //             {selectedSection === 'payoff' && <ProjectCardPayoff project={project} />}
        //             {selectedSection === 'discounts' && <ProjectCardDiscounts project={project} />}
        //             {selectedSection === 'getting-it-done' && <ProjectGettingItDone project={project} />}
        //             {selectedSection === 'resources' && <ProjectResources project={project} resources={enriched_resources} />}
        //         </div>
        //     </div>
        // </div>
    );
};

export default ProjectCard;